import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import './BugReport.css';

interface BugReportProps {
  onNavigate: (page: string) => void;
  onClose?: () => void;
  isModal?: boolean;
  showToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

const BugReport: React.FC<BugReportProps> = ({ onNavigate, onClose, isModal = false, showToast }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'bug' | 'feature' | 'improvement' | 'other'>('bug');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !title || !description) return;
    
    setIsSubmitting(true);

    try {
      // Submit bug report
      const { error } = await supabase.from('bug_reports').insert({
        user_id: user.id,
        title,
        description,
        category,
        priority,
      });

      if (error) throw error;

      setSubmitSuccess(true);
      
      if (showToast) {
        showToast('Bug report submitted successfully! Our team will review it soon.', 'success');
      }
      
      // Reset form
      setTimeout(() => {
        setTitle('');
        setDescription('');
        setCategory('bug');
        setPriority('medium');
        setSubmitSuccess(false);
        
        if (isModal && onClose) {
          onClose();
        } else {
          onNavigate('dashboard');
        }
      }, 2000);

    } catch (error) {
      if (showToast) {
        showToast('Failed to submit bug report. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      onNavigate('dashboard');
    }
  };

  if (submitSuccess) {
    return (
      <div className={`bug-report ${isModal ? 'modal' : ''}`}>
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2>Thank You!</h2>
          <p>Your report has been submitted successfully.</p>
          <p>Our team will review it soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bug-report ${isModal ? 'modal' : ''}`}>
      <div className="bug-report-header">
        <button onClick={handleClose} className="back-btn">
          ← {isModal ? 'Close' : 'Back'}
        </button>
        <h1>Report Bug/Suggestion</h1>
      </div>

      <form onSubmit={handleSubmit} className="bug-report-form">
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief description of the bug or suggestion"
            required
            maxLength={255}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value as typeof category)}
            >
              <option value="bug">Bug</option>
              <option value="feature">Feature Suggestion</option>
              <option value="improvement">Improvement Suggestion</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as typeof priority)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description *</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please describe the bug or suggestion in detail. For bugs, include steps to reproduce. For suggestions, explain the benefit."
            required
            rows={6}
          />
        </div>


        <div className="form-actions">
          <button type="button" onClick={handleClose} className="cancel-btn">
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className="submit-btn">
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BugReport;